import { Flex, Modal, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import {
  type PaymentDetailsDto,
  useLazyGetClientPaymentInfoQuery,
} from '@app/api/ClientPayments';
import { ACQUIRING, BANK, CASH } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type { NClientPaymentsList } from '@app/pages/Clients/ClientPayments/ClientPaymentsList/ClientPaymentsList.types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { divideAmount, formatISODateToCustomFormat } from '@app/utils';

const { Text } = Typography;

export const PaymentInfoModal: React.FC<
  NClientPaymentsList.PaymentInfoModalProps
> = ({ open, setOpen, objectId, setObjectId }) => {
  const [paymentInfo, setPaymentInfo] = useState<PaymentDetailsDto | null>(
    null
  );

  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [getClientPaymentInfo] = useLazyGetClientPaymentInfoQuery();

  const handlePaymentInfoModalOpen = useCallback(async () => {
    try {
      if (!objectId) return showNotification('Id платежа не найден');

      const result = await getClientPaymentInfo({
        ActiveBranchId,
        paymentId: objectId,
      }).unwrap();

      setPaymentInfo(result);
      setOpen(true);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    ActiveBranchId,
    getClientPaymentInfo,
    objectId,
    setOpen,
    showNotification,
  ]);

  useEffect(() => {
    if (handlePaymentInfoModalOpen && objectId) {
      handlePaymentInfoModalOpen();
    }
  }, [handlePaymentInfoModalOpen, objectId]);

  const handleCancel = () => {
    setOpen(false);
    setPaymentInfo(null);
    setObjectId(null);
  };

  const getPaymentTypeLabel = (paymentType?: string | null): string => {
    switch (paymentType) {
      case CASH:
        return 'наличные';
      case BANK:
        return 'банковская карта';
      case ACQUIRING:
        return 'эквайринг';
      default:
        return '-';
    }
  };

  const getOperationLabel = (operation?: string | null): string => {
    switch (operation) {
      case ACQUIRING:
        return 'эквайринг';
      default:
        return '-';
    }
  };

  const getStatusLabel = (operation?: string | null): string => {
    switch (operation) {
      case 'succeeded':
        return 'успешно';
      case 'waiting_for_capture':
        return 'ожидание капчи';
      case 'canceled':
        return 'отменено';
      case 'pending':
        return 'выполняется';
      default:
        return '-';
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title="Информация о платеже"
      footer={null}
    >
      <Flex vertical gap={16}>
        {paymentInfo?.PaymentType === ACQUIRING && (
          <Flex justify="space-between">
            <Text>Id платежа</Text>
            <Text>{paymentInfo?.ExternalId || '-'}</Text>
          </Flex>
        )}
        <Flex justify="space-between">
          <Text>Тип операции</Text>
          <Text>{getOperationLabel(paymentInfo?.Operation) || '-'}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text>Дата операции</Text>
          <Text>
            {paymentInfo?.Date
              ? formatISODateToCustomFormat(paymentInfo.Date)
              : '-'}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text>Дата создания</Text>
          <Text>
            {paymentInfo?.TimeCreated
              ? formatISODateToCustomFormat(paymentInfo.TimeCreated)
              : '-'}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text>Сумма</Text>
          <Text>
            {paymentInfo?.Amount ? divideAmount(paymentInfo.Amount) : '-'}
          </Text>
        </Flex>
        <Flex justify="space-between">
          <Text>Код способа оплаты</Text>
          <Text>{getPaymentTypeLabel(paymentInfo?.PaymentType) || '-'}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text>Статус</Text>
          <Text>{getStatusLabel(paymentInfo?.Status) || '-'}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text>Номер записи</Text>
          <Text>{paymentInfo?.RecordNumber || '-'}</Text>
        </Flex>
      </Flex>
    </Modal>
  );
};
